/* eslint-disable react/require-default-props */
import React, {
    DetailedHTMLProps,
    SelectHTMLAttributes,
    useEffect,
    useState,
} from 'react'
import { createUseStyles } from 'react-jss'
import { colors } from '../../../theme/palette'

export interface ISelectOptions {
    label: string
    value: string
}

export type SelectProps3 = {
    options: ISelectOptions[]
    useControlled?: boolean
    isOpen?: boolean
    hasError?: boolean
    hideBorder?: boolean
    hideArrow?: boolean
} & Omit<
    DetailedHTMLProps<
        SelectHTMLAttributes<HTMLSelectElement>,
        HTMLSelectElement
    >,
    'size'
>

const useStyles = createUseStyles({
    wrap: {
        position: 'relative',
        height: '3.125rem',
        backgroundColor: colors.white,
        border: `1px solid ${colors.borderColor}`,
        borderRadius: '6px',
        overflow: 'hidden',
    },
    select: {
        position: 'relative',
        height: '3.125rem',
        border: 0,
        width: '100%',
        fontSize: '1rem',
        paddingTop: '.75rem',
        paddingLeft: '.5rem',
        paddingRight: '1rem',
        cursor: 'pointer',
        color: colors.black,

        '& option': {
            color: colors.black,
        },

        '&:focus-visible': {
            outline: 'none',
        },
        '&:focus-within + label:not([class^="label"])': {
            color: colors.green500,
            fontSize: '0.75rem',
            height: 'auto',
            width: 'auto',
            backgroundColor: 'transparent',
        },
        '&:focus + label + label:not([class^="label"])': {
            borderColor: colors.green500,
            outlineColor: colors.green500,
            paddingTop: '12px',
        },
        '&:active + label + label:not([class^="label"])': {
            borderColor: colors.green500,
            outlineColor: colors.green500,
            paddingTop: '12px',
        },
        '&:disabled + label': {
            color: colors.labelColor,
            backgroundColor: colors.gray500,
            '& + span': {
                color: colors.labelColor,
            },
        },
        '&:disabled': {
            appearance: 'none',
            WebkitAppearance: 'none',
            MozAppearance: 'none',
            backgroundImage: 'none',
            cursor: 'not-allowed',
        },
    },
    hasValue: {
        color: colors.green500,
        fontSize: '0.75rem',
        height: 'auto',
        width: 'auto',

        transitionDuration: '300ms',
        pointerEvents: 'none',
        position: 'absolute',
        top: '0',
        left: '0',
        zIndex: 1,
        paddingTop: '0.17rem',
        paddingLeft: '.75rem',
        paddingRight: '1rem',
        cursor: 'pointer',
    },
    label: {
        transitionDuration: '300ms',
        pointerEvents: 'none',
        position: 'absolute',
        top: '0',
        left: '0',
        width: 'calc(100% - 20px)',
        height: '100%',
        zIndex: 1,
        fontSize: '1rem',
        paddingTop: '.75rem',
        paddingLeft: '0.75rem',
        paddingRight: '1rem',
        cursor: 'pointer',
        backgroundColor: colors.white,
        color: colors.labelColor,
    },
})

const Select = React.forwardRef<HTMLSelectElement, SelectProps3>(
    (
        {
            options,
            onChange,
            defaultValue,
            name,
            placeholder,
            isOpen,
            useControlled,
            hasError,
            hideBorder,
            hideArrow,
            disabled,
            ...props
        },
        ref
    ) => {
        const classes = useStyles()
        const [query, setQuery] = useState<string | number | readonly string[]>(
            defaultValue
        )

        useEffect(() => {
            if (useControlled) {
                setQuery(props.value)
            }
        }, [props.value, useControlled])

        useEffect(() => {
            if (defaultValue) {
                setQuery(defaultValue)
            }
        }, [defaultValue])

        // useEffect(() => {
        //     if (isOpen !== undefined && !isOpen) {
        //         setQuery('')
        //     }
        // }, [isOpen])

        return (
            <div
                className={classes.wrap}
                style={{
                    ...{
                        ...(hideBorder && { padding: '0 .2rem 0 0' }),
                        color: colors.black,
                        border: `1px solid ${
                            !hideBorder ? colors.borderColor : 'transparent'
                        }`,
                        backgroundColor: colors.white,
                    },
                }}
            >
                <select
                    ref={ref}
                    name={name}
                    id={name}
                    disabled={disabled}
                    {...props}
                    className={classes.select}
                    defaultValue={defaultValue}
                    onChange={(e) => {
                        setQuery(e.target.value)
                        onChange?.(e)
                    }}
                    style={{
                        color:
                            hasError && !disabled ? colors.error : colors.black,
                        ...(hideArrow && {
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            MozAppearance: 'none',
                        }),
                        ...(hideBorder && {
                            color:
                                hasError && !disabled ? colors.error : '#000',
                        }),
                        ...(disabled && {
                            backgroundColor: colors.disabledBackground,
                            opacity: 1,
                        }),
                    }}
                >
                    {!defaultValue ? (
                        <option value="" hidden>
                            {' '}
                        </option>
                    ) : null}
                    {options.map(({ label, value }) => (
                        <option key={value} value={value}>
                            {label}
                        </option>
                    ))}
                </select>

                <label
                    style={{
                        ...{
                            ...(hideBorder && {
                                color: colors.black,
                            }),
                            ...(hideArrow && {
                                paddingTop: 0,
                                paddingLeft: '0.5rem',
                                paddingRight: '1rem',
                            }),
                            color:
                                hasError && !disabled
                                    ? colors.error
                                    : colors.black,
                            backgroundColor: disabled
                                ? colors.disabledBackground
                                : hasError && !disabled
                                ? 'transparent'
                                : colors.white,
                        },
                    }}
                    htmlFor={name}
                    className={
                        query && query !== '' ? classes.hasValue : classes.label
                    }
                >
                    {placeholder || name}
                </label>
            </div>
        )
    }
)

export { Select }
